import { useCallback, useEffect, useState } from "react";
import { Link, useRouter } from "../router";
import { AgentChat } from "./agent/AgentChat";
import { startThread } from "./agent/AgentDrawer";
import { agentApi, type AgentConfig, type AgentThread } from "./agent/agentApi";
import "./agent/agent.css";

export const ASSISTANT_PATH = "/app/assistant";

// The "Assistant" area (M15): all of the advisor's conversations with Anders'
// assistant, and a full-width conversation.
//   /app/assistant        → newest conversation (or an empty start)
//   /app/assistant/{id}   → one conversation
export function Assistant() {
  const { pathname, navigate } = useRouter();
  const rest = pathname.slice(ASSISTANT_PATH.length).replace(/^\/+|\/+$/g, "");
  const selected = /^\d+$/.test(rest) ? Number(rest) : null;
  const [threads, setThreads] = useState<AgentThread[] | null>(null);
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setThreads((await agentApi.threads()).threads);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load conversations.");
    }
  }, []);

  useEffect(() => {
    void load();
    agentApi.config().then(setConfig).catch(() => {});
  }, [load]);

  const create = async () => {
    try {
      const t = await startThread(undefined);
      await load();
      navigate(`${ASSISTANT_PATH}/${t.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start a conversation.");
    }
  };

  const remove = async (id: number) => {
    try {
      await agentApi.remove(id);
      await load();
      if (selected === id) navigate(ASSISTANT_PATH);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete.");
    }
  };

  // Keep the list's title/cost in step with the open conversation.
  const onThread = useCallback((t: AgentThread) => {
    setThreads((prev) => (prev ? prev.map((x) => (x.id === t.id ? { ...x, ...t } : x)) : prev));
  }, []);

  const active = selected ?? threads?.[0]?.id ?? null;

  return (
    <div className="page agent-page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Assistant</span>
          <h1>Anders' assistant</h1>
          <p className="muted">
            Q&amp;A over Andersen and Quantfolio, and advice drafts for you to review in Advisory.
            {config && (
              <>
                {" "}
                {config.fallbackReason ? "Demo mode (scripted)" : config.model} · Quantfolio via MCP · cap $
                {config.maxCostPerThread.toFixed(2)} per conversation
              </>
            )}
          </p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn btn-primary" onClick={create}>
            New conversation
          </button>
        </div>
      </header>
      {error && <p className="agent-error">{error}</p>}
      <div className="agent-area">
        <nav className="agent-threads" aria-label="Conversations">
          {threads === null && <p className="muted agent-pad">Loading…</p>}
          {threads?.length === 0 && <p className="muted agent-pad">No conversations yet.</p>}
          {threads?.map((t) => (
            <div key={t.id} className={`agent-thread-link ${t.id === active ? "active" : ""}`}>
              <Link to={`${ASSISTANT_PATH}/${t.id}`}>
                <span className="agent-thread-title">{t.title ?? "New conversation"}</span>
                <span className="agent-thread-meta">
                  {t.investor_name ? `${t.investor_name} · ` : ""}${t.cost.toFixed(2)}
                  {t.advisory_session_id ? ` · advice #${t.advisory_session_id}` : ""}
                </span>
              </Link>
              <button type="button" className="agent-thread-del" onClick={() => void remove(t.id)} aria-label="Delete conversation">
                ×
              </button>
            </div>
          ))}
        </nav>
        <section className="agent-main">
          {active ? (
            <AgentChat
              key={active}
              threadId={active}
              onThread={onThread}
              suggestions={[
                "Summarise Erik Holmberg's holdings and his open advice flows",
                "Prepare an Investment Advice for Erik Holmberg — retirement in 10 years",
              ]}
            />
          ) : (
            threads && (
              <div className="agent-empty agent-pad">
                <p className="muted">Start a conversation to ask about a client or prepare an advice.</p>
                <button type="button" className="btn btn-ghost btn-sm" onClick={create}>
                  New conversation
                </button>
              </div>
            )
          )}
        </section>
      </div>
      <p className="agent-foot muted">AI-prepared drafts are reviewed and submitted by the advisor · Powered by Quantfolio</p>
    </div>
  );
}
